import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../services/axiosInstance";

export const fetchBlogs = createAsyncThunk("blogs/fetchAll", async () => {
  const response = await axiosInstance.get("/api/blogs");
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data?.blogs)) return response.data.blogs;
  return [];
});

const initialState = {
  byId: {},
  allIds: [],
  status: "idle",
  error: null,
};

const blogsSlice = createSlice({
  name: "blogs",
  initialState,
  reducers: {
    addBlog: (state, action) => {
      const blog = action.payload;
      if (!blog?.id) return;
      state.byId[blog.id] = blog;
      if (!state.allIds.includes(blog.id)) {
        state.allIds.push(blog.id);
      }
    },
    updateBlog: (state, action) => {
      const blog = action.payload;
      if (!blog?.id) return;
      state.byId[blog.id] = { ...state.byId[blog.id], ...blog };
      if (!state.allIds.includes(blog.id)) {
        state.allIds.push(blog.id);
      }
    },
    removeBlog: (state, action) => {
      const blogId = action.payload;
      if (!blogId) return;
      delete state.byId[blogId];
      state.allIds = state.allIds.filter((id) => id !== blogId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlogs.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        state.byId = {};
        state.allIds = [];

        action.payload.forEach((blog) => {
          state.byId[blog.id] = blog;
          state.allIds.push(blog.id);
        });

        state.status = "succeeded";
        state.error = null;
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const { addBlog, updateBlog, removeBlog } = blogsSlice.actions;

export const selectBlogsById = (state) => state.blogs.byId;
export const selectAllBlogIds = (state) => state.blogs.allIds;
export const selectBlogsStatus = (state) => state.blogs.status;
export const selectBlogsError = (state) => state.blogs.error;
const selectBlogIdParam = (_, id) => id;

export const selectAllBlogs = createSelector(
  [selectAllBlogIds, selectBlogsById],
  (allIds, byId) => allIds.map((id) => byId[id]).filter(Boolean)
);

export const selectBlogById = createSelector(
  [selectBlogsById, selectBlogIdParam],
  (byId, id) => byId[id]
);

export const selectPublishedCount = createSelector([selectAllBlogs], (blogs) =>
  blogs.reduce((count, blog) => count + (blog.published === true ? 1 : 0), 0)
);

export const selectBlogsSortedByDate = createSelector([selectAllBlogs], (blogs) =>
  [...blogs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
);

export default blogsSlice.reducer;
